import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export type TeamTone = 'sea' | 'coral'

type CupTriangleProps = {
  layout: number[][]
  taken: Set<number>
  /** Colore della squadra: mare in casa, corallo in trasferta (i due biglietti della home). */
  tone: TeamTone
  /** Squadra in trasferta: capovolge l'ordine delle righe così la punta resta rivolta verso la rete al centro
   * del tavolo; determina anche il lato nel `data-testid`. */
  reversed?: boolean
  /** Fase del pareggio: i bicchieri rimasti di questa squadra (il "vincente") diventano oro. */
  gold?: boolean
  /** Nuovo spostamento appena registrato: i bicchieri rimasti lampeggiano argento per 2 secondi. */
  silverFlash?: boolean
  /** Spostamento in corso su questa squadra: posizioni vuote che possono ricevere un bicchiere ("+"). */
  targets?: ReadonlySet<number>
  /** Bicchieri già spostati in questa sessione (anteprima, non ancora confermata): anello tenue. */
  moved?: ReadonlySet<number>
  /** Bicchiere scelto come `from` a metà di uno spostamento: più grande, con l'anello. */
  selected?: number | null
  onCupClick?: (cupIndex: number) => void
  disabled?: boolean
}

const TONE_CLASS: Record<TeamTone, string> = { sea: 'bg-sea', coral: 'bg-coral' }

/**
 * Triangolo dei bicchieri di una squadra, di sola lettura salvo `onCupClick`. Bicchieri da 56px (64px con tre
 * bicchieri): si tirano col pollice anche col telefono in una mano. Un bicchiere preso resta come impronta
 * tratteggiata col suo numero, così l'arbitro vede sempre le posizioni.
 */
export function CupTriangle({
  layout,
  taken,
  tone,
  reversed = false,
  gold = false,
  silverFlash = false,
  targets,
  moved,
  selected = null,
  onCupClick,
  disabled = true,
}: CupTriangleProps) {
  const { t } = useTranslation()
  const side = reversed ? 'away' : 'home'
  const rows = reversed ? [...layout].reverse() : layout
  const isSmallRack = layout.flat().length <= 3

  return (
    <div className="flex flex-col items-center gap-2.5">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="flex justify-center gap-2.5">
          {row.map((cupIndex) => {
            const isTaken = taken.has(cupIndex)
            const isTarget = targets?.has(cupIndex) ?? false
            return (
              <button
                key={cupIndex}
                type="button"
                disabled={disabled || !onCupClick}
                onClick={() => onCupClick?.(cupIndex)}
                data-testid={`cup-${side}-${cupIndex}`}
                aria-label={t('zambePong.match.cup', { n: cupIndex + 1 })}
                aria-pressed={selected === cupIndex || undefined}
                className={cn(
                  isSmallRack ? 'size-16' : 'size-14',
                  'grid place-items-center rounded-full font-body text-xs font-bold tabular-nums transition-[transform,opacity,background-color,box-shadow,border-color] duration-300 ease-out-expo disabled:cursor-default enabled:active:scale-90',
                  isTaken
                    ? 'scale-90 border-2 border-dashed border-paper/20 text-paper/30'
                    : cn(TONE_CLASS[tone], 'text-ink/70 shadow-[inset_0_-5px_0_rgb(0_0_0/0.22),inset_0_0_0_3px_rgb(255_255_255/0.25)]'),
                  // Oro (token --color-gold) deliberatamente diverso da --color-sun: si riconosce da ogni triangolo.
                  !isTaken && gold && 'bg-gold shadow-[0_0_28px_-6px_var(--color-gold)] motion-safe:animate-pulse',
                  // Argento = il token --color-line esistente (bordi/linee), non un grigio inventato.
                  !isTaken && silverFlash && 'bg-line motion-safe:animate-pulse',
                  isTarget && 'border-paper/70 text-paper',
                  moved?.has(cupIndex) && 'ring-2 ring-paper/60 ring-offset-2 ring-offset-ink',
                  selected === cupIndex && 'scale-110 ring-4 ring-paper ring-offset-4 ring-offset-ink',
                )}
              >
                {isTarget ? '+' : cupIndex + 1}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
